import { opsCntrolCom } from "../../com";
import { uniquePositionedCargo } from "./unique-positioned-cargo";
import { uniqueSeatedPersons } from "./unique-seated-persons";
import { opsMissionTypeEnum, payloadCodeTypeEnum, targetPlatformEnum } from "../../dataStruct";
import { markDownProcessing as MarkDownProcessing, parseCss, reconcileCollection, runCommands } from "../../functions";
import { NamedCommandOp } from "../../../../services/database/named-command-batch";
import { rowIdFor } from "../../../../db/rowid";
import { isMissingProcessDbError } from "../../../../services/database/http-error";
import { fppProvider as FppProvider, payLoadStruct } from "./fppProvider";

// A 409 from the app-server's /command route is NOT necessarily a benign LWW
// no-op: dispatch.ts maps every CLASSIFIED applyOps reject (lww, not-found,
// immutable, "may not sign", and the SAVEPOINT-throw path's arbitrary
// bind/constraint error message) to HTTP 409 with `{error: reason}` in the body.
// Not every reject, though — since efb-app-server 7c7514a a reject flagged
// serverFault (the kernel ACKed but the local projection refused the row) is a
// **500** and a transient one a **503**, so do not treat "not 200" as "409". Swallowing
// every 409 would mask real write faults (silent server-side data loss). Only
// `reason === 'lww'` is a true no-op — discriminate on the reason, re-throw the
// rest. The reason is the browser HttpError's JSON body (database.service.browser.ts);
// native never 409s (local SQLite), so this branch only runs on web.
function isBenignLwwConflict(err: unknown): boolean {
  if ((err as { status?: number })?.status !== 409) return false;
  const body = (err as { body?: unknown })?.body;
  if (typeof body !== "string") return false;
  try {
    return (JSON.parse(body) as { error?: unknown })?.error === "lww";
  } catch {
    return false;
  }
}

/**
 * Read the published (CIS) half of a flight-crew-controller entry, degrading to
 * `[]` on ANY failure so a device without the update pack — or a server that
 * doesn't know the entry — keeps working off local rows.
 *
 * Never narrow this to a 404: an unknown catalog entry is a **400**, a
 * non-`web` entry a **403** and a SQL fault a **500** (`efb-app-server`
 * `src/api/dispatch.ts`). A 404-only predicate misses all three, and each one
 * then propagates out of `fillDataFields` — which is awaited OUTSIDE the
 * try/finally that clears `isProgress`, leaving `/duty-ofp-limits` stuck on a
 * progress bar with no dialog and no way to sign. That was tried and reverted.
 *
 * Module-level so `opsTemplateProvider` shares one implementation.
 */
export async function queryPublishedRoster(classCom: any, name: string, params?: any): Promise<any[]> {
  try {
    return (await classCom.namedQuery(name, params)) ?? [];
  } catch (err) {
    if (isMissingProcessDbError(err)) {
      console.warn(`[fcc] ${name}: published roster not installed, using local rows only`);
    } else {
      console.error(`[fcc] ${name}: published roster read FAILED, falling back to local rows only`, err);
    }
    return [];
  }
}

// Flight Crew Controller Plugin
export class FlightCrewStruct {
  dutyId: string;
  created: number;
  updated: number;
  origin: string;
  dutyStartTime: number;
  dutyEndTime: number;
  dutyOverlap: number;
  aircraftRegistration: string;
  homeBaseIdent: string;
  pilotsAboard: string;
  parameters: string;
  dutyDesig: string;
  processReport: string;
}

export class PayLoadStruct {
  active: boolean = true;
  name: string;
  priority: number;
  description: string;
  codeType: payloadCodeTypeEnum;
  weightValue: number;
  weightOrigValue: number; // this var is for change detection
  weightMin: number;
  weightMax: number;
  weightMaxUsable: number;
  weightMaxUsableOei: number;
  unitLoad: payloadUnitEnum;
  loadArmX: number;
  loadArmY: number;
  unitLoadArm: payloadLoadArmUnitEnum;
  origin: string;
  parameters: string;
  lookupObj: any;
}
export enum payloadUnitEnum {
  kg = 0,
  l,
  lbs,
  gal,
}
export enum payloadLoadArmUnitEnum {
  mm = 0,
  m,
  inches,
}

export class CompanyCrewListStruct {
  crewMemberId: number;
  crewMemberDesig: string;
}

export class CrewCargokeyValueStruct {
  crewMemberId: number;
  keyName: string;
  value: string;
}

/**
 * Is this duty id absent?
 *
 * The one predicate every duty-id guard uses, exported so the page that creates
 * a flight, `initFlight` and the readers below cannot drift apart on what
 * "missing" means. They did drift once: `getFlightCrewIdOfDutyDesig` returns the
 * EMPTY STRING when no row matches, while the caller tested `== undefined`, and
 * `"" == undefined` is false -- so a flight was created with an empty crew id
 * and every later mass-and-balance read matched nothing.
 *
 * Tests the VALUE, never truthiness. `0` is a REAL duty id: an ad-hoc duty is
 * written with dutyId 0 (addReplaceAhocDuty) and is the pilot's escape hatch
 * when no CIS duty is readable, so `!dutyId` would refuse the number form and
 * take that hatch away in the exact situation it exists for.
 */
export function isMissingDutyId(dutyId: unknown): boolean {
  return dutyId === undefined || dutyId === null || String(dutyId).trim() === "";
}

export class fccProvider {
  /** Mission configurations shipped in the FCC pack for this aircraft, excluding local duties. */
  async getPublishedOpsMissionTypes(aircraftRegistration: string, classCom: any): Promise<string[]> {
    const bases = await this.queryPublishedRoster(classCom, "dutyDistinctHomebasesCis");
    const duties = (
      await Promise.all(
        bases.map((base) =>
          this.queryPublishedRoster(classCom, "dutyByHomebaseCis", { homebaseIdent: base.homeBaseIdent })
        )
      )
    ).flat();
    const types = new Set<string>();
    for (const duty of duties) {
      if (duty.aircraftRegistration !== aircraftRegistration) continue;
      // FCC serializes the list as opsMissionTypes:ems_day,ems_night;...
      const entry = String(duty.parameters ?? "")
        .split(";")
        .find((part) => part.split(":")[0].trim() === "opsMissionTypes");
      for (const name of (entry?.slice(entry.indexOf(":") + 1) ?? "").replace(/[\[\]]/g, "").split(",")) {
        const type = name.trim();
        if (type && typeof opsMissionTypeEnum[type] === "number" && type !== "all") types.add(type);
      }
    }
    return [...types];
  }

  async getPublishedAircraftLoading(aircraftRegistration: string, classCom: any): Promise<PayLoadStruct[]> {
    const bases = await this.queryPublishedRoster(classCom, "dutyDistinctHomebasesCis");
    const duties = (
      await Promise.all(
        bases.map((base) =>
          this.queryPublishedRoster(classCom, "dutyByHomebaseCis", { homebaseIdent: base.homeBaseIdent })
        )
      )
    )
      .flat()
      .filter((duty) => duty.aircraftRegistration === aircraftRegistration);
    const ids = [...new Set(duties.map((duty) => String(duty.dutyId)))];
    return (await Promise.all(ids.map((id) => this.getEntireLoading(id, false, classCom, undefined, true)))).flat();
  }

  async getDutyIdOfFlight(flightId: string, classCom: any): Promise<string> {
    try {
      const result = await classCom.namedQuery("flightlistByFlightId", { flightId });

      let useIdx: number = 0;
      for (let li = 0; li < result.length; li++) {
        if (result[li].dutyId == "0") {
          useIdx = li;
        } //prefer changes duties with id = 0
      }

      return result[useIdx].flightCrewId;
    } catch (err) {
      console.log("flight crew of flight id: " + flightId + " not found! " + err);
    }
  }
  async getAircraftRegOfDuty(dutyId: string, classCom: any): Promise<string> {
    try {
      const result = await this.queryWithPublished(classCom, "dutyAircraftRegByDutyId", { dutyId });

      if (result.length == 0) {
        console.log("No aircraft registration found for dutyId: " + dutyId);
        return "";
      }
      return result[0].aircraftRegistration;
    } catch (error) {
      console.error("Error fetching aircraft registration:", error);
      return "";
    }
  }
  async getFlightCrewIdOfDutyDesig(
    dutyDesig: string,
    classCom: any,
    returnTheChangedCrew_if_avail: boolean = false
  ): Promise<string> {
    try {
      const result = await this.queryWithPublished(classCom, "dutyIdsByDesig", { dutyDesig });

      if (result.length == 0) {
        return "";
      }

      let useIdx: number = 0;
      if (returnTheChangedCrew_if_avail) {
        for (let li = 0; li < result.length; li++) {
          if (result[li].dutyId == "0") {
            useIdx = li;
          } //prefer changes duties with id = 0
        }
      }

      return result[useIdx].dutyId;
    } catch (err) {
      console.log("flight crew of flight id: " + dutyDesig + " not found! " + err);
    }
  }

  // adhoc duties
  async dropAdhocDuty(classCom) {
    // Crew: soft-delete every live member of the adhoc duty (reconcile to ∅).
    await reconcileCollection(
      "crewmemberdataRowIdsByDutyId",
      { dutyId: "0" },
      "crewmemberdataDeleteByRowId",
      new Set<string>()
    );
    await this.dropLocalAdhocDutyRow(classCom);
  }

  /**
   * The drop of the duty page load. A device without the duty row (a logout
   * deleted it, or it did not sync yet) drops nothing: the crew then belongs to
   * the devices that still hold the duty, and the delete would sync to them.
   */
  async dropAdhocDutyIfHeld(classCom) {
    const held = (await classCom.namedQuery("dutyByDutyId", { dutyId: "0" })) ?? [];
    if (held.length > 0) await this.dropAdhocDuty(classCom);
  }

  /**
   * Deletes the ad-hoc duty row on this device only (native); the crew rows stay.
   * The next login on this device then has to sign, while every other device of
   * the aircraft keeps its duty. duties has no soft-delete / web removal by
   * design — it relies on upsert-in-place — so on web this entry is refused;
   * swallow that.
   */
  async dropLocalAdhocDutyRow(classCom) {
    try {
      await classCom.namedCommand("dutyDeleteByDutyId", { dutyId: "0" });
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status !== 403 && status !== 409) throw err;
    }
  }
  async addReplaceAhocDuty(
    entireLoading: payLoadStruct[],
    parameters: string,
    dutyDesig: string,
    classCom: opsCntrolCom,
    pilotAboard: string,
    dutyStartTime: number,
    dutyEndTime: number,
    homebase: string = ""
  ) {
    // Validate before the first write. Published rows may omit optional KV fields.
    const loading = entireLoading.map((item, index) => {
      if (item.codeType == null || item.weightValue == null || !Number.isFinite(Number(item.weightValue))) {
        throw new Error(`Duty loading item ${index + 1}: code type and numeric mass are required`);
      }
      return {
        ...item,
        name: item.name ?? "",
        description: item.description ?? "",
        parameters: item.parameters ?? "",
        unitLoad: item.unitLoad ?? 0,
        weightOrigValue: item.weightOrigValue ?? item.weightValue,
        active: item.active ?? true,
      };
    });
    // The adhoc duty is the singleton scratch duty, always dutyId='0'. dutyId is
    // bound to a TEXT pk on the server (STRICT), so stringify rather than passing
    // the numeric 0.
    const dutyIdStr = "0";
    const created: number = new Date().getTime();
    const updated: number = created;
    const origin: string = "efb-max adhoc-duty";
    // The duty lasts exactly as long as the signature the caller just wrote: sign + 24 h
    // for an ad-hoc duty, the managed window for a crew change. A fixed 60 days kept it
    // current for the next crew after the automatic logout (Igor, 2026-09-28).
    const dutyOverlap: number = 120;
    const aircraftRegistration: string = classCom.aircraftRegistration;
    const homeBaseIdent: string = homebase;
    const pilotsAboard_: string = pilotAboard;
    const parameters_: string = parameters;
    const dutyDesig_: string = dutyDesig;
    const processReport: string = "## this is a ad-hoc duty on this device. \n";

    // Upsert-in-place on dutyId='0' (no delete-then-insert). updatedAt is a
    // server-only param (the mobile duties table has no updatedAt column — the
    // SQL doesn't bind it; native ignores the extra param), required by the web
    // sync upsert.
    try {
      await classCom.namedCommand("dutyInsert", {
        dutyId: dutyIdStr,
        created,
        updated,
        origin,
        dutyStartTime,
        dutyEndTime,
        dutyOverlap,
        aircraftRegistration,
        homeBaseIdent,
        pilotsAboard: pilotsAboard_,
        parameters: parameters_,
        dutyDesig: dutyDesig_,
        processReport,
        updatedAt: created,
      });
    } catch (err) {
      // 409 with reason 'lww' = benign no-op: the app-server already holds an
      // equal-or-newer dutyId='0' row, so the upsert was a no-op. Keep going
      // (the crew rebuild below still runs). Any OTHER 409 (not-found, a bind/
      // constraint fault surfaced via ops.ts's SAVEPOINT-throw path, etc.) is a
      // real write fault and must NOT be swallowed — it would silently drop the
      // duty header server-side. Native never 409s (local SQLite).
      if (!isBenignLwwConflict(err)) throw err;
    }

    // The crew rebuild uses the live crewMemberIds of this duty again, in id
    // order, so a save updates the stored rows in place. A fresh id per save
    // marked the whole crew list deleted and added a new one each time
    // (22,096 deleted rows on one aircraft, 2026-10-06). Only a member past the
    // stored count gets a new id. Upsert each, collect the live set, then
    // reconcile away the rows of members that left.
    // On web the upserts go as one request: one per key-value row was about
    // 270 serial requests (13 s) per sign on a BK117 (dev-ara, 2026-09-26).
    // runCommands skips a 'lww' reject and throws on every other one.
    const keepCrew = new Set<string>();
    const crewOps: NamedCommandOp[] = [];
    const updatedAt: number = new Date().getTime();
    const storedIds = await this.adhocCrewIds(dutyIdStr, classCom);
    let crewMemberId: number = Math.max(new Date().getTime(), ...storedIds.map((id) => Number(id) + 1));

    for (let li = 0; li < loading.length; li++) {
      const thisCrewMemberId = storedIds[li] ?? String(crewMemberId++);
      const thisItem = loading[li];

      // all parameters in a row
      // also original weight and active, to
      // be able to load the state
      // ===================================

      const kvs: Array<{ keyName: string; value: string }> = [
        { keyName: "codeType", value: thisItem.codeType.toString() },
        { keyName: "name", value: thisItem.name.toString() },
        { keyName: "description", value: thisItem.description.toString() },
        { keyName: "weightValue", value: thisItem.weightValue.toString() },
        { keyName: "parameters", value: thisItem.parameters.toString() },
        { keyName: "unitLoad", value: thisItem.unitLoad.toString() },
        { keyName: "weightOrigValue", value: thisItem.weightOrigValue.toString() },
        { keyName: "active", value: thisItem.active.toString() },
      ];
      for (const kv of kvs) {
        const rowId = rowIdFor([dutyIdStr, thisCrewMemberId, kv.keyName]);
        keepCrew.add(rowId);
        crewOps.push({
          name: "crewmemberdataInsert",
          params: {
            rowId,
            crewMemberId: thisCrewMemberId,
            dutyId: dutyIdStr,
            keyName: kv.keyName,
            value: kv.value,
            updatedAt,
          },
        });
      }
    }
    await runCommands(crewOps, false, true);

    await reconcileCollection(
      "crewmemberdataRowIdsByDutyId",
      { dutyId: dutyIdStr },
      "crewmemberdataDeleteByRowId",
      keepCrew,
      true
    );
  }

  /**
   * The live crew ids of a local duty, lowest first. The ids are save times, so
   * this is the order of the list that was saved. A failed read answers no id:
   * the save then uses new ids, as every save did before.
   */
  private async adhocCrewIds(dutyId: string, classCom: opsCntrolCom): Promise<string[]> {
    try {
      const rows: Array<{ crewMemberId?: unknown }> = await classCom.namedQuery("crewmemberdataByDuty", { dutyId });
      const ids = new Set<string>();
      for (const row of rows) {
        const id = String(row?.crewMemberId ?? "").trim();
        if (id !== "" && Number.isFinite(Number(id))) ids.add(id);
      }
      return [...ids].sort((a, b) => Number(a) - Number(b));
    } catch (err) {
      console.error("addReplaceAhocDuty: could not read the stored crew ids: ", err);
      return [];
    }
  }

  /**
   * Read the CIS half of a duty/crew query — the published roster, which the
   * update pipeline delivers as a process DB.
   *
   * ALWAYS degrades to `[]` — the efb_user half is queried right after, and no
   * failure of the published half may take the duty page down with it. The four
   * callers are awaited bare: `listAllHomebases` from `fillDataFields`,
   * `listAllDutiesAtHomebase` from `updateHomebase`, `getCompanyCrewList` from
   * `loadChangedCrewData`, `getCrewMemberData` from `selectPic`. On the
   * `initialize()` path the first three run outside the try/finally that clears
   * `isProgress` (left true by `ngOnInit`), so a throw there leaves the page on
   * a permanent progress bar with no error dialog and no way to sign.
   * Rethrowing was tried (review round 1) and reverted: the
   * three failures it meant to surface are not 404s (unknown catalog entry is a
   * **400**, `!entry.web` a **403**, a SQL fault a **500** — `efb-app-server/
   * src/api/dispatch.ts`), so the predicate missed all of them and every one
   * bricked the page instead.
   *
   * The visibility half of that contract is kept by severity, not by throwing:
   * an absent process DB is expected (`warn`), anything else is a real fault
   * (`error`). Note that is CONSOLE-ONLY — `sentry.ts` registers no
   * `captureConsoleIntegration`, and nothing throws here, so no Sentry event is
   * produced. A degraded roster is therefore indistinguishable from an empty
   * one at the operator's seat. Closing that needs a caller-side banner plus a
   * `Sentry.captureMessage` on this branch; tracked in the workspace `TODO.md`
   * under the published-roster section, not fixed here.
   *
   * Platform note: on native this wrapper is mostly moot. `selectProcess`
   * returns `[]` for an unpopulated DB and swallows its own errors, and
   * `selectProcessWithParams` already absorbs the 404 — so native reaches the
   * catch only for genuine faults on the param-carrying entries.
   */
  private async queryPublishedRoster(classCom: any, name: string, params?: any): Promise<any[]> {
    return queryPublishedRoster(classCom, name, params);
  }

  /**
   * Read a flight-crew-controller table across BOTH halves: the published
   * roster (`<name>Cis`, shipped in the update pack) and this device's local
   * `efb_user` rows, concatenated **CIS first**.
   *
   * Phase D moved these reads into the catalog keeping only the `efb_user`
   * half, but nothing imports the published rows into `efb_user` — so a duty
   * that came from the roster resolved to nothing: no crew, no loading, 0 kg,
   * and (since today's guard) a "duty not available" block on signing. See
   * `src/app/db/catalog.ts` § `…Cis` and the repo-root `CLAUDE.md`.
   *
   * CIS first matches the four reads converted earlier: where a caller dedupes
   * first-wins, the *published* row is authoritative for the roster. Rows are
   * NOT deduped here — callers that care already do it. The `dutyId`-keyed
   * entries cannot collide (local crew/duty writes carry the ad-hoc sentinel
   * `dutyId = '0'`, or the dev-bypass id, while published rows carry real duty
   * ids), but `dutyIdsByDesig` keys on `dutyDesig`, which the ad-hoc duty
   * deliberately COPIES from the published one — so both halves return a row
   * and CIS-first is what makes `result[0]` resolve to the published dutyId.
   * That is load-bearing, not incidental: the local-only read resolved to
   * `'0'` and dead-ended `updateDutyIdOfFlight`.
   *
   * The published half degrades to `[]` on any failure, so a device without
   * the pack behaves exactly as it did before.
   */
  /**
   * Split the ids of a `queryWithPublished` result into the two different
   * things callers need from them.
   *
   * `bind` keeps every DISTINCT RAW form. The same params object is bound
   * against the CIS process DB and against `efb_user`, and the two declare
   * `crewMemberId` with different types — `efb_user` declares it `text`, the
   * CIS schema ships inside the update pack. SQLite does not match a bound
   * string against an INTEGER column or the reverse, so binding only `123`
   * silently drops the `"123"` rows in the other half.
   *
   * `keys` holds one normalized string per person or item, and is what the
   * dedupe, the maps and the outer loops must agree on: `Array.includes` and
   * `Set.has` are SameValueZero, so `123` and `"123"` are two entries and the
   * same crew member was built, counted and weighed twice.
   */
  private splitCrewIds(rows: any[]): { bind: unknown[]; keys: string[] } {
    const bind: unknown[] = [];
    const keys: string[] = [];
    const seenRaw = new Set<string>();
    const seenKey = new Set<string>();

    for (let i = 0; i < rows.length; i++) {
      const raw = rows[i].crewMemberId;

      const rawTag = typeof raw + ":" + String(raw);
      if (seenRaw.has(rawTag) === false) {
        seenRaw.add(rawTag);
        bind.push(raw);
      }

      const key = String(raw);
      if (seenKey.has(key) === false) {
        seenKey.add(key);
        keys.push(key);
      }
    }

    return { bind, keys };
  }

  private async queryWithPublished(classCom: any, name: string, params?: any): Promise<any[]> {
    // Published duty 0 is the person directory, while local duty 0 is an actual
    // ad-hoc loading. Never merge directory candidates into that loading.
    if (params?.dutyId !== undefined && params?.dutyId !== null && String(params.dutyId).trim() === "0") {
      return (await classCom.namedQuery(name, params)) ?? [];
    }
    // Both halves start together; the published rows still come first.
    const published = this.queryPublishedRoster(classCom, `${name}Cis`, params);
    published.catch(() => undefined);
    const local: Promise<any[]> = classCom.namedQuery(name, params);
    local.catch(() => undefined);
    return [...(await published), ...((await local) ?? [])];
  }

  async listAllHomebases(classCom: any): Promise<string[]> {
    const result = [
      ...(await this.queryPublishedRoster(classCom, "dutyDistinctHomebasesCis")),
      ...(await classCom.namedQuery("dutyDistinctHomebases")),
    ];

    const retArr: string[] = [];

    for (let i = 0; i < result.length; i++) {
      if (!retArr.includes(result[i].homeBaseIdent)) {
        retArr.push(result[i].homeBaseIdent);
      }
    }

    // order the array alphabetically:
    retArr.sort((a, b) => a.localeCompare(b));

    return retArr;
  }

  async listAllDutiesAtHomebase(homebaseIdent: string, classCom: any): Promise<string[]> {
    const result = [
      ...(await this.queryPublishedRoster(classCom, "dutyByHomebaseCis", { homebaseIdent })),
      ...(await classCom.namedQuery("dutyByHomebase", { homebaseIdent })),
    ];

    const retArr: string[] = [];

    for (let i = 0; i < result.length; i++) {
      if (!retArr.includes(result[i].dutyDesig)) {
        retArr.push(result[i].dutyDesig);
      }
    }

    // order the array alphabetically:
    retArr.sort((a, b) => a.localeCompare(b));

    return retArr;
  }

  async getCompanyCrewList(classCom: any): Promise<CompanyCrewListStruct[]> {
    const fcs: CompanyCrewListStruct[] = [];
    const result = [
      ...(await this.queryPublishedRoster(classCom, "historicCrewMembersAllCis")),
      ...(await classCom.namedQuery("historicCrewMembersAll")),
    ];

    // Dedupe on crewMemberId: the same person can exist in both halves once a
    // locally-created crewmember also appears in a later published roster. The
    // old `!fcs.includes(newFC)` never matched — `newFC` is a fresh object
    // every iteration — which was harmless while only one source was read.
    const seen = new Set<string>();
    for (let i = 0; i < result.length; i++) {
      const res = result[i];
      const key = String(res.crewMemberId);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);

      const newFC = new CompanyCrewListStruct();
      newFC.crewMemberId = res.crewMemberId;
      newFC.crewMemberDesig = res.name;
      fcs.push(newFC);
    }

    return fcs;
  }

  async getListCargoConfigs(dutyId: string, paraFilter: string, classCom: any): Promise<CrewCargokeyValueStruct[]> {
    const fcs: CrewCargokeyValueStruct[] = [];
    const result = await this.queryWithPublished(classCom, "crewmemberdataParametersByDuty", { dutyId });

    const typeLst: string[] = [];
    for (let i = 0; i < result.length; i++) {
      const newFC = new CrewCargokeyValueStruct();
      const type = parseCss(result[i].value, paraFilter);

      if (type != "" && type != undefined) {
        if (typeLst.includes(type) == false) {
          typeLst.push(type);

          newFC.value = type;
          fcs.push(newFC);
        }
      }
    }

    return fcs;
  }

  /**
   * Refuse a read that would weigh the aircraft against a duty id that is not there.
   *
   * On this line the SQL is bound by the named-query catalog, so a missing id is
   * not a syntax error and not an injection -- it simply matches no row, and the
   * reader returns an empty load. On a page that weighs an aircraft, an id that
   * is not there must not read as "nothing aboard", so it has to be loud.
   *
   * Guards the three readers that feed mass-and-balance. Elsewhere an empty
   * selection is legitimate (a duty designator before one has been picked) and
   * must keep returning nothing rather than throwing.
   */
  private requireDutyId(dutyId: string): void {
    if (isMissingDutyId(dutyId)) {
      throw new Error("fccProvider: refusing to read a loading with no dutyId");
    }
  }

  async getBookedCargo(dutyId: string, paraFilter: string, classCom: any): Promise<PayLoadStruct[]> {
    this.requireDutyId(dutyId);
    const codetype: number = payloadCodeTypeEnum.cargo;

    const result = await this.queryWithPublished(classCom, "crewmemberdataIdsByDutyCodeType", {
      dutyId,
      codeType: codetype,
    });

    // Deduplicate, for exactly the reason `getScheduledPaxAboard` below does:
    // this read spans the published CIS roster AND the local efb_user copy,
    // concatenated, so an item present in both halves comes back twice. The
    // loop below builds one `PayLoadStruct` per entry, so a double-listed cargo
    // item was weighed into mass-and-balance twice. See `splitCrewIds` for why
    // the bind list and the iteration list are not the same array.
    const { bind: cargoBindIds, keys: crewMemberId } = this.splitCrewIds(result);

    if (crewMemberId.length === 0) {
      return [];
    }

    const resultAll = await this.queryWithPublished(classCom, "crewmemberdataByDutyAndIds", {
      dutyId,
      crewMemberIds: cargoBindIds,
    });

    // loop the prebuild array to assign all db values of a member to one entry (as they come in batches)
    const resultArr: PayLoadStruct[] = [];
    for (let i = 0; i < crewMemberId.length; i++) {
      const cmi: string = crewMemberId[i];

      const newPayload: PayLoadStruct = new PayLoadStruct();
      newPayload.active = true;
      // loop sql result
      for (let m = 0; m < resultAll.length; m++) {
        const cmiSql = resultAll[m];

        if (cmiSql.crewMemberId == cmi) {
          switch (cmiSql.keyName) {
            case "name": {
              newPayload.name = cmiSql.value;
              break;
            }
            case "weightValue": {
              newPayload.weightValue = Number(cmiSql.value);
              break;
            }
            case "unitLoad": {
              newPayload.unitLoad = Number(cmiSql.value);
              break;
            }
            case "parameters": {
              newPayload.parameters = cmiSql.value;
              break;
            }

            case "codeType": {
              newPayload.codeType = cmiSql.value;
              break;
            }

            case "active": {
              newPayload.active = cmiSql.value;
              break;
            }
          }
        }
      }

      if (newPayload.parameters.includes(paraFilter) || paraFilter == undefined) {
        if (newPayload.active) {
          resultArr.push(newPayload);
        }
      }
    }

    return resultArr;
  }

  async getScheduledPaxAboard(dutyId: string, classCom: any) {
    this.requireDutyId(dutyId);
    const result = await this.queryWithPublished(classCom, "crewmemberdataIdsByDutyCodeTypes", {
      dutyId,
      codeTypes: [payloadCodeTypeEnum.fo, payloadCodeTypeEnum.crew, payloadCodeTypeEnum.pax, payloadCodeTypeEnum.pic],
    });

    if (result.length === 0) return 0;

    // Deduplicate. The read spans the published CIS roster AND the local
    // efb_user copy, concatenated — a crew member who exists in both halves
    // appears twice, and the counting loop below walks THIS array, so every
    // double-listed person was counted twice: five people aboard a five-seat
    // helicopter reported POB 10. The sibling readers of the same union,
    // `getEntireLoading` and `getBookedCargo`, deduplicated but on the RAW
    // value, which is the same defect wearing a dedupe: both are fixed too.
    //
    // Deduplicated and keyed on a STRING, here and in the two maps below.
    // See `splitCrewIds` for why, and for why the list bound into the next
    // query is a different array.
    //
    const { bind: paxBindIds, keys: crewIds } = this.splitCrewIds(result);

    const rowsBulk = await this.queryWithPublished(classCom, "crewmemberdataByDutyAndIds", {
      dutyId,
      crewMemberIds: paxBindIds,
    });

    const personById = new Map<string, any>(
      crewIds.map((id) => [id, { codeType: undefined, name: "", parameters: "", active: true, weight: NaN }])
    );
    for (let i = 0; i < rowsBulk.length; i++) {
      const row = rowsBulk[i];
      const person = personById.get(String(row.crewMemberId));
      if (!person) continue;
      if (row.keyName === "active") {
        person.active = row.value == "true";
      } else if (row.keyName === "weightValue") {
        person.weight = parseInt(row.value);
      } else if (row.keyName === "codeType" || row.keyName === "name" || row.keyName === "parameters") {
        person[row.keyName] = row.value ?? "";
      }
    }

    // A device can hold the crew of an earlier duty sign beside the current one
    // (#963). Those copies have different ids, so count each person at a seat once.
    const pax_cnt = uniqueSeatedPersons([...personById.values()]).filter((p) => p.active && p.weight > 0).length;

    return pax_cnt;
  }
  async getEntireLoading(
    dutyId: string,
    onlyActiveItems: boolean = false,
    classCom: any,
    codeType?: payloadCodeTypeEnum,
    publishedOnly: boolean = false
  ): Promise<PayLoadStruct[]> {
    return this.loadingOf(await this.readLoading(dutyId, classCom, publishedOnly), onlyActiveItems, codeType);
  }

  /**
   * Every crew row of a duty, in one read. A caller that needs several code types
   * reads once and calls `loadingOf` per type: one request instead of two per type.
   */
  async readLoading(dutyId: string, classCom: any, publishedOnly: boolean = false): Promise<any[]> {
    this.requireDutyId(dutyId);
    if (publishedOnly && String(dutyId).trim() === "0") return [];
    return publishedOnly
      ? this.queryPublishedRoster(classCom, "crewmemberdataByDutyCis", { dutyId })
      : this.queryWithPublished(classCom, "crewmemberdataByDuty", { dutyId });
  }

  loadingOf(resultAll: any[], onlyActiveItems: boolean, codeType?: payloadCodeTypeEnum): PayLoadStruct[] {
    // Same filter the SQL used (`CAST(value AS INTEGER) = @codeType`): SQLite reads the
    // leading integer, gives 0 when there is none, and NULL never matches.
    const result =
      codeType == undefined
        ? resultAll
        : resultAll.filter(
            (r) => r.keyName === "codeType" && r.value != null && (parseInt(String(r.value), 10) || 0) === codeType
          );

    // This is the mass-and-balance reader — `focMain` builds the loading table
    // and the trim from it — and it deduped on the RAW value, so the CIS and
    // efb_user halves of the union yielded two entries for one person and a
    // whole crew member's mass was added to the aircraft. Same defect as
    // `getScheduledPaxAboard`; see `splitCrewIds`.
    const { keys: crewMemberId } = this.splitCrewIds(result);

    let resultArr: PayLoadStruct[] = [];
    if (crewMemberId.length == 0) {
      return resultArr;
    }

    // loop the prebuild array to assign all db values of a member to one entry (as they come in batches)

    for (let i = 0; i < crewMemberId.length; i++) {
      const cmi: string = crewMemberId[i];

      const newPayload: PayLoadStruct = new PayLoadStruct();
      newPayload.active = true;

      // loop sql result

      // check whether is not yet set (required for default)

      let activeHasBeenSet: boolean = false;
      for (let m = 0; m < resultAll.length; m++) {
        const cmiSql = resultAll[m];

        if (cmiSql.crewMemberId == cmi) {
          switch (cmiSql.keyName) {
            case "name": {
              newPayload.name = cmiSql.value;
              break;
            }

            case "weightValue": {
              newPayload.weightValue = Number(cmiSql.value);
              newPayload.weightOrigValue = Number(cmiSql.value);
              break;
            }
            case "unitLoad": {
              newPayload.unitLoad = Number(cmiSql.value);
              break;
            }
            case "parameters": {
              newPayload.parameters = cmiSql.value;
              break;
            }
            case "codeType": {
              newPayload.codeType = cmiSql.value;
              break;
            }

            case "description": {
              newPayload.description = cmiSql.value;
              break;
            }

            case "active": {
              newPayload.active = cmiSql.value == "true";
              activeHasBeenSet = true;
              break;
            }
          }
        }
      }

      if (!activeHasBeenSet) {
        if (newPayload.parameters !== undefined) {
          if (newPayload.parameters.includes("default:false")) {
            newPayload.active = false;
          }
        }
      }

      // Partial legacy KV fragments are not loading definitions. Do not pass
      // them to the editor or signing writer; zero remains a valid unknown mass.
      if (newPayload.codeType == null || newPayload.weightValue == null) continue;
      newPayload.name = newPayload.name ?? "";
      newPayload.description = newPayload.description ?? "";
      newPayload.parameters = newPayload.parameters ?? "";

      // do not return 0kg items on "only-active items"
      if (!resultArr.includes(newPayload)) {
        if (onlyActiveItems) {
          if (newPayload.weightValue > 0) {
            resultArr.push(newPayload);
          }
        } else {
          resultArr.push(newPayload);
        }
      }
    }

    resultArr = uniqueSeatedPersons(uniquePositionedCargo(resultArr));
    if (onlyActiveItems) {
      for (let i = 0; i < resultArr.length; i++) {
        if (resultArr[i].active == false) {
          resultArr[i].name = " ~~" + resultArr[i].name + "~~ ";
          resultArr[i].weightValue = 0;
        }
      }
    }

    // resultArr.sort(dynamicSort("weightValue"));

    return resultArr;
  }

  async getPassengersNotOnAircraft(dutyId: string, classCom: any): Promise<CrewCargokeyValueStruct[]> {
    const fcs: CrewCargokeyValueStruct[] = [];

    return fcs;
  }

  async getCargoNotOnAircraft(dutyId: string, classCom: any): Promise<CrewCargokeyValueStruct[]> {
    const fcs: CrewCargokeyValueStruct[] = [];

    return fcs;
  }

  async getCrewAndCargo(dutyId: string, classCom: any): Promise<CrewCargokeyValueStruct[]> {
    const fcs: CrewCargokeyValueStruct[] = [];
    const result = await this.queryWithPublished(classCom, "crewmemberdataByDuty", { dutyId });

    for (let i = 0; i < result.length; i++) {
      const newFC = new CrewCargokeyValueStruct();
      const res = result[i];
      newFC.crewMemberId = res.crewMemberId;
      newFC.keyName = res.keyName;
      newFC.value = res.value;

      if (!fcs.includes(newFC)) {
        fcs.push(newFC);
      }
    }

    return fcs;
  }

  async getCrewMemberData(crewmemberId: number, classCom: any): Promise<CrewCargokeyValueStruct[]> {
    const fcs: CrewCargokeyValueStruct[] = [];
    const result = [
      ...(await this.queryPublishedRoster(classCom, "crewmemberdataByMemberIdCis", { crewmemberId })),
      ...(await classCom.namedQuery("crewmemberdataByMemberId", { crewmemberId })),
    ];

    for (let i = 0; i < result.length; i++) {
      const newFC = new CrewCargokeyValueStruct();
      const res = result[i];
      newFC.crewMemberId = res.crewMemberId;
      newFC.keyName = res.keyName;
      newFC.value = res.value;

      if (!fcs.includes(newFC)) {
        fcs.push(newFC);
      }
    }

    return fcs;
  }
  /**
   * The `description` values of many crew members, keyed by `String(crewMemberId)`,
   * published rows first. One read per source instead of two per person. If the
   * batched entry fails (an app-server older than this entry answers 400), it reads
   * each person as before.
   */
  async getCrewMemberDescriptions(crewMemberIds: unknown[], classCom: any): Promise<Map<string, string[]>> {
    const byId = new Map<string, string[]>();
    if (crewMemberIds.length === 0) return byId;
    const add = (id: unknown, value: string) => {
      const key = String(id);
      byId.set(key, [...(byId.get(key) ?? []), value]);
    };
    // Both forms of each id: the column holds numbers in one copy and strings in
    // the other, and a bound value matches only its own type (see `splitCrewIds`).
    const bind = [
      ...new Set(crewMemberIds.flatMap((id) => [id, String(id), ...(Number.isFinite(Number(id)) ? [Number(id)] : [])])),
    ];
    try {
      const [published, local] = await Promise.all([
        classCom.namedQuery("crewmemberdataDescriptionsByMemberIdsCis", { crewMemberIds: bind }),
        classCom.namedQuery("crewmemberdataDescriptionsByMemberIds", { crewMemberIds: bind }),
      ]);
      for (const row of [...(published ?? []), ...(local ?? [])]) add(row.crewMemberId, row.value);
    } catch (err) {
      console.warn("batched crew descriptions failed, reading each person:", err);
      byId.clear();
      await Promise.all(
        crewMemberIds.map(async (id) => {
          for (const row of await this.getCrewMemberData(id as number, classCom)) {
            if (row.keyName === "description") add(id, row.value);
          }
        })
      );
    }
    return byId;
  }

  /** The current and upcoming duties of an aircraft. A crew-change sign spans every row of its
   *  designator, and the day half of a split duty is not active yet when its night half is signed. */
  async getCurrentAndFutureDuties(referenceTime: number, aircraftReg: string, classCom: any): Promise<any[]> {
    return this.queryWithPublished(classCom, "dutyCurrentAndFutureForAircraft", {
      referenceTime,
      aircraftRegistration: aircraftReg,
    });
  }

  async getCurrentFlightCrew(referenceTime: number, aircraftReg: string, classCom: any): Promise<FlightCrewStruct[]> {
    const fcs: FlightCrewStruct[] = [];

    const result = await this.queryWithPublished(classCom, "dutyActiveAtTimeForAircraft", {
      referenceTime,
      aircraftRegistration: aircraftReg,
    });

    for (let i = 0; i < result.length; i++) {
      const newFC = new FlightCrewStruct();
      const res = result[i];
      newFC.dutyId = res.dutyId;
      newFC.created = res.created;
      newFC.updated = res.updated;
      newFC.origin = res.origin;
      newFC.dutyStartTime = res.dutyStartTime;
      newFC.dutyEndTime = res.dutyEndTime;
      newFC.dutyOverlap = res.dutyOverlap;
      newFC.homeBaseIdent = res.homeBaseIdent;
      newFC.pilotsAboard = res.pilotsAboard;
      newFC.parameters = res.parameters;
      newFC.dutyDesig = res.dutyDesig;
      newFC.processReport = res.processReport;
      fcs.push(newFC);
    }

    return fcs;
  }

  /**
   * Neutralize raw HTML in a duty's `processReport` before it becomes markdown.
   *
   * The rendered result reaches `bypassSecurityTrustHtml` in
   * `md-preview-page.component.ts`, and showdown passes embedded HTML through
   * (no sanitizer configured). `processReport` is NOT trusted: `dutyInsert` is a
   * `web: true` catalog entry whose `@processReport` bind the server takes
   * verbatim (`dispatch.ts resolveEntry` overrides only `registrationId`), so
   * any device authenticated to a registration can write markup that every peer
   * in that room then renders in the app origin.
   *
   * Only `<` is escaped, so all markdown syntax survives. That is deliberately
   * PARTIAL and is not the load-bearing control — escaping `<` blocks a literal
   * `<script>`, but the payload need not contain a tag at all: showdown turns
   * the pure-markdown `[x](javascript:…)` into a live `href`. The real check is
   * `markDownProcessing.neutralizeUnsafeUrls`, which allowlists URL schemes on
   * the RENDERED output, where every markdown path converges — including the
   * six sibling `{nav,met,notam,perf,fuel,loading}.processReport` readers, whose
   * writers are `web: true` + synced too (the earlier "locally generated"
   * reasoning for skipping them was wrong).
   *
   * Losslessness was measured on the CIS pack (0 of 105 `duties.processReport`
   * values contain a `<`). Note the escaped population here is actually the
   * **efb_user** duties table — `dutyCurrentAndFutureForAircraft` is
   * `db: 'efb_user'` — which is the peer-writable half and was not measured.
   * Kept anyway: `<` in a duty report has no legitimate use, and the flightops
   * `*_nav` reports that DO encode line breaks as literal `<br/>` go through
   * `navLogReport.ts`, not this path.
   */
  private escapeReportHtml(report: string): string {
    return String(report ?? "").replace(/</g, "&lt;");
  }

  async getCrewHtml(referenceTime: number, acftReg: string, classCom: any): Promise<string> {
    if (referenceTime == undefined) {
      referenceTime = new Date().getTime();
    }

    const htmlRes: string[] = [];

    let toggle: boolean = false;

    // Each half carries its own `ORDER BY dutyStartTime ASC`, which the union
    // does not preserve — re-sort so the rendered list stays chronological.
    const result = (
      await this.queryWithPublished(classCom, "dutyCurrentAndFutureForAircraft", {
        referenceTime,
        aircraftRegistration: acftReg,
      })
    ).sort((a: any, b: any) => (a?.dutyStartTime ?? 0) - (b?.dutyStartTime ?? 0));

    for (let i = 0; i < result.length; i++) {
      const newFC = new FlightCrewStruct();
      const res = result[i];

      toggle = !toggle;

      if (toggle) {
        htmlRes.push("");
        htmlRes.push("");
        htmlRes.push("|>");
        htmlRes.push("");
        htmlRes.push("");
        htmlRes.push(this.escapeReportHtml(res.processReport).replace(/\\n/g, "\n"));
      } else {
        htmlRes.push("");
        htmlRes.push("");
        htmlRes.push("|<");
        htmlRes.push("");
        htmlRes.push("");
        htmlRes.push(this.escapeReportHtml(res.processReport).replace(/\\n/g, "\n"));
      }
    }

    let html: string = "";
    for (let i = 0; i < htmlRes.length; i++) {
      html += "\n" + (htmlRes[i] + "\n").trim();
    }

    // console.log(html);

    const mdp = new MarkDownProcessing();
    html = await mdp.convMarkdown(
      html,
      classCom.appearance,
      undefined,
      opsMissionTypeEnum.all,
      classCom,
      new FppProvider()
    );

    if (classCom.targetPlatform == targetPlatformEnum.server) {
      const fs = classCom.package_fs;
      fs.writeFile("out.html", html, function (err) {
        if (!err) {
          const open = classCom.package_open;
          open("out.html");
        }
      });
    }

    return html;
  }
}
